import { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { onSnapshot, doc } from "firebase/firestore";
import { ChatData, ChatType } from "../types/chat";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";

/**
 * Converts various timestamp formats to milliseconds
 * Handles Firestore Timestamps, epoch numbers, ISO strings, and {seconds, nanos} objects
 */
function toMillis(v: any): number {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (typeof v?.toMillis === "function") return v.toMillis(); // Firestore Timestamp
  if (typeof v?.seconds === "number") return v.seconds * 1000; // {seconds, nanos}
  const t = new Date(v).getTime(); // ISO string fallback
  return Number.isFinite(t) ? t : 0;
}

interface UseChatMessagesResult {
  messages: ChatData[];
  isLoading: boolean;
  error: Error | null;
  chatType: ChatType;
  pendingMessages: ChatData[];
  setPendingMessages: React.Dispatch<React.SetStateAction<ChatData[]>>;
  renderMessages: ChatData[];
}

/**
 * Hook to manage chat messages with real-time Firestore sync
 * Extracted from mobile (Chat.tsx) - using mobile implementation as source of truth
 * 
 * Features:
 * - Real-time message fetching via onSnapshot
 * - Message deduplication by clientId
 * - Pending message reconciliation for optimistic UI
 * - Automatic sorting by timestamp
 * 
 * @param chatId - The ID of the chat to fetch messages from
 * @returns Object containing messages, loading state, error state, and pending messages
 */
export function useChatMessages(chatId: string | null): UseChatMessagesResult {
  const [messages, setMessages] = useState<ChatData[]>([]);
  const [pendingMessages, setPendingMessages] = useState<ChatData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [chatType, setChatType] = useState<ChatType>("");

  // Setup listener for chat messages
  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setPendingMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const chatDocRef = doc(db, FB_COLLECTION_PATH.Chat, chatId);

    const unsubscribe = onSnapshot(
      chatDocRef,
      (msgDoc) => {
        const data = msgDoc.data();

        if (!data) {
          setMessages([]);
          setIsLoading(false);
          return;
        }

        // Keep chatType updated for future sends
        setChatType(data.chatCategory || "");

        const rawMessages = Array.isArray(data.messages) ? data.messages : [];

        const mappedRaw: ChatData[] = rawMessages.map((msg: any, idx: number) => {
          const createdAt = toMillis(msg?.docStamp?.timestamp?.createdAt ?? msg?.docStamp?.timestamp?.updatedAt);
          const updatedAt = toMillis(msg?.docStamp?.timestamp?.updatedAt ?? createdAt);

          const clientId =
            typeof msg?.chatContent?.clientId === "string" && msg.chatContent.clientId.trim()
              ? msg.chatContent.clientId.trim()
              : undefined;

          // More stable fallback id for legacy messages (no clientId)
          const fromUid = msg?.docStamp?.userInfoFrom?.uid || "unknown";
          const textSeed = (msg?.chatContent?.text || "").slice(0, 24);
          const fallbackId = `${fromUid}_${createdAt}_${textSeed || "msg"}_${idx}`;

          return {
            id: clientId || fallbackId,
            clientId,
            pending: false,
            failed: false,

            IsRead: false,
            MessageAuthorId: fromUid,
            MessageAuthor: `${msg?.docStamp?.userInfoFrom?.firstName || ""} ${
              msg?.docStamp?.userInfoFrom?.lastName || ""
            }`.trim(),
            MessageText: msg?.chatContent?.text || "",
            JobDocId: msg?.docStamp?.docInfo?.parent?.docID || chatId,
            UserActionTakenId: 0,
            UserActionTakenUserId: 0,
            createdAt,
            updatedAt,
            MessageAuthorPic: msg?.docStamp?.userInfoFrom?.profilePicURL || "",
            NewChatPath: msg?.docStamp?.docInfo?.parent?.docPath || "",
          };
        });

        // Deduplicate server duplicates by clientId (keep earliest createdAt)
        const seenClient = new Map<string, ChatData>();
        const deduped: ChatData[] = [];

        for (const m of mappedRaw) {
          if (m.clientId) {
            const existing = seenClient.get(m.clientId);
            if (!existing) {
              seenClient.set(m.clientId, m);
            } else {
              // Keep the earliest createdAt (or keep the first if equal)
              if ((m.createdAt || 0) < (existing.createdAt || 0)) {
                seenClient.set(m.clientId, m);
              }
            }
          } else {
            deduped.push(m);
          }
        }

        // Add clientId-based messages back in
        seenClient.forEach((v) => deduped.push(v));

        setMessages(deduped);
        setIsLoading(false);

        // Reconcile pending in state (remove those confirmed by server)
        const serverClientIds = new Set<string>();
        deduped.forEach((m) => {
          if (m.clientId) serverClientIds.add(m.clientId);
        });

        setPendingMessages((prev) =>
          prev.filter((p) => {
            if (!p.clientId) return true;
            return !serverClientIds.has(p.clientId);
          })
        );
      },
      (err) => {
        console.error("Chat listener error:", err);
        setError(err as Error);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [chatId]);

  // Combine messages and pendingMessages for rendering
  const renderMessages = useMemo(() => {
    const serverClientIds = new Set(messages.map((m) => m.clientId).filter(Boolean) as string[]);

    const filteredPending = pendingMessages.filter((p) => {
      if (!p.clientId) return true;
      return !serverClientIds.has(p.clientId);
    });

    const combined = [...messages, ...filteredPending];

    return [...combined].sort((a, b) => {
      const aTime = typeof a.createdAt === "number" ? a.createdAt : 0;
      const bTime = typeof b.createdAt === "number" ? b.createdAt : 0;
      return aTime - bTime;
    });
  }, [messages, pendingMessages]);

  return {
    messages,
    isLoading,
    error,
    chatType,
    pendingMessages,
    setPendingMessages,
    renderMessages,
  };
}
